import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, CheckCircle2, XCircle, ExternalLink, MapPin, Mail, Globe, Sparkles, Copy, Link2, Clock, LogIn } from "lucide-react";
import { api, formatApiErrorDetail, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const inputCls = "bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100";

// Staff: invite a dealership by email (branded link → pre-filled signup) or just copy the link.
export function InviteDealerDialog({ open, onOpenChange, onSent }) {
  const [form, setForm] = useState({ email: "", dealer_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const close = (o) => { onOpenChange(o); if (!o) setTimeout(() => { setResult(null); setForm({ email: "", dealer_name: "", note: "" }); }, 300); };
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/invites", form);
      setResult(data);
      onSent?.(data);
      toast[data.email_status === "sent" ? "success" : "warning"](data.email_status === "sent" ? `Invite emailed to ${data.email}` : "Invite created — the email didn't go out, copy the link instead");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const copy = (link) => navigator.clipboard?.writeText(link).then(() => toast.success("Invite link copied"));
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="invite-dealer-dialog" className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#0A8CF2]" /> Invite a dealership</DialogTitle>
          <DialogDescription className="text-slate-400">They get a branded email with a link to the 4-step signup, pre-filled with their name and email. You approve them here once they finish.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="invite-result">
            <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Invite {result.email_status === "sent" ? "sent to" : "created for"} <b>{result.email}</b></div>
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-1">Signup link</div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-[#7FC3FF] truncate flex-1" data-testid="invite-link">{result.link}</code>
                <Button size="sm" variant="outline" onClick={() => copy(result.link)} data-testid="invite-copy-btn" className="h-8 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <Button variant="ghost" onClick={() => close(false)} data-testid="invite-done-btn" className="w-full text-slate-400 hover:text-white">Done</Button>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-3" data-testid="invite-form">
            <div>
              <Label className="text-slate-300 mb-1.5 block">Dealer contact email</Label>
              <Input required type="email" data-testid="invite-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="gm@dealership.com" className={inputCls} />
            </div>
            <div>
              <Label className="text-slate-300 mb-1.5 block">Dealership name <span className="text-slate-500 font-normal">(optional)</span></Label>
              <Input data-testid="invite-name-input" value={form.dealer_name} onChange={(e) => setForm({ ...form, dealer_name: e.target.value })} placeholder="Abeloff Kia" className={inputCls} />
            </div>
            <div>
              <Label className="text-slate-300 mb-1.5 block">Personal note <span className="text-slate-500 font-normal">(optional)</span></Label>
              <Textarea data-testid="invite-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Great talking today — here's the link we discussed." className={`${inputCls} min-h-[70px]`} />
            </div>
            <Button type="submit" disabled={busy || !form.email.trim()} data-testid="invite-send-btn" className="w-full h-10 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" /> Send invite</>}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ dealer, onOpenChange, onDeclined }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decline = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/dealers/${dealer.id}`, { status: "declined", note });
      toast.success(`${dealer.name} declined`);
      onDeclined?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!dealer} onOpenChange={onOpenChange}>
      <DialogContent data-testid="decline-dialog" className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2"><XCircle className="h-5 w-5 text-red-400" /> Decline {dealer?.name}?</DialogTitle>
          <DialogDescription className="text-slate-400">Their logins are signed out and blocked, and their admin gets an email. You can still approve them later or remove them.</DialogDescription>
        </DialogHeader>
        <Label className="text-slate-300 mb-1.5 block">Note to the dealer <span className="text-slate-500 font-normal">(optional)</span></Label>
        <Textarea data-testid="decline-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="We couldn't verify the dealership website…" className={`${inputCls} min-h-[70px]`} />
        <div className="flex gap-2 justify-end mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-white">Cancel</Button>
          <Button onClick={decline} disabled={busy} data-testid="decline-confirm-btn" className="bg-red-600 hover:bg-red-500 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decline"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Top of the Platform page: who's waiting for approval + invite button + open invites.
export function DealerOnboardingPanel({ dealers, busy, onApprove, onEnter, onChanged }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [declining, setDeclining] = useState(null);
  const [invites, setInvites] = useState([]);
  const loadInvites = () => api.get("/platform/invites").then(({ data }) => setInvites(data)).catch(() => {});
  useEffect(() => { loadInvites(); }, [dealers]);
  const pending = dealers.filter((d) => d.status === "pending");
  const open = invites.filter((i) => !i.used_at);
  return (
    <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5 mb-6" data-testid="onboarding-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading font-bold flex items-center gap-2"><UserPlus className="h-4 w-4 text-[#0A8CF2]" /> New dealerships</h2>
          <p className="text-xs text-slate-400 mt-0.5">Dealers join on their own at <a href="/signup" target="_blank" rel="noreferrer" className="text-[#7FC3FF] hover:text-white">/signup</a> (or from your invite) and wait here until you approve them. You're emailed the moment one signs up.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-dealer-btn" className="h-9 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold shrink-0"><Mail className="mr-1.5 h-4 w-4" /> Invite a dealer</Button>
      </div>

      {pending.length === 0 ? (
        <p className="text-sm text-slate-500 rounded-lg border border-dashed border-[#2A2E3D] px-4 py-5 text-center" data-testid="no-pending">No dealerships waiting for approval.</p>
      ) : (
        <div className="space-y-2" data-testid="pending-list">
          {pending.map((d) => (
            <div key={d.id} data-testid={`pending-dealer-${d.id}`} className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{d.name}</span>
                  <Badge variant="outline" className="border-amber-700/50 bg-amber-950/40 text-amber-400 uppercase text-[10px] font-mono-tech">awaiting approval</Badge>
                  {d.addon_specialist_match === "requested" && <Badge variant="outline" className="border-[#0A8CF2]/50 text-[#7FC3FF] text-[10px] font-mono-tech flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" /> wants Specialist Match</Badge>}
                  {d.invited_by && <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px] font-mono-tech">invited</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap mt-1">
                  {d.website_url && <a href={d.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white"><Globe className="h-3 w-3" /> {d.website_url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" /></a>}
                  {(d.city || d.address) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[d.city, d.state].filter(Boolean).join(", ") || d.address}</span>}
                  {d.admin_email && <span className="inline-flex items-center gap-1" data-testid={`pending-admin-${d.id}`}><Mail className="h-3 w-3" /> {d.admin_name ? `${d.admin_name} · ` : ""}{d.admin_email}</span>}
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> signed up {timeAgo(d.created_at)} · {d.vehicles} vehicles synced</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap shrink-0">
                <Button size="sm" onClick={() => onApprove(d)} disabled={busy === d.id} data-testid={`approve-dealer-${d.id}`} className="h-9 bg-emerald-600 hover:bg-emerald-500 font-semibold"><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => setDeclining(d)} disabled={busy === d.id} data-testid={`decline-dealer-${d.id}`} className="h-9 border-red-900/50 bg-red-950/30 hover:bg-red-950/60 text-red-400"><XCircle className="mr-1.5 h-3.5 w-3.5" /> Decline</Button>
                <Button size="sm" variant="outline" onClick={() => onEnter(d)} data-testid={`review-dealer-${d.id}`} className="h-9 border-[#0A8CF2]/50 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF]"><LogIn className="mr-1.5 h-3.5 w-3.5" /> Review dashboard</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open.length > 0 && (
        <div className="mt-4" data-testid="open-invites">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-2">Open invites · {open.length}</div>
          <div className="space-y-1.5">
            {open.map((i) => (
              <div key={i.id} data-testid={`invite-${i.id}`} className="flex items-center gap-3 text-xs text-slate-300 rounded-md border border-slate-800 bg-slate-950 px-3 py-2">
                <Link2 className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                <span className="truncate flex-1">{i.email}{i.dealer_name ? ` · ${i.dealer_name}` : ""} <span className="text-slate-500">· sent {timeAgo(i.created_at)}{i.email_status === "failed" ? " · email failed" : ""}</span></span>
                <button type="button" onClick={() => navigator.clipboard?.writeText(i.link).then(() => toast.success("Invite link copied"))} data-testid={`invite-copy-${i.id}`} className="text-slate-400 hover:text-white inline-flex items-center gap-1"><Copy className="h-3 w-3" /> copy link</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <InviteDealerDialog open={inviteOpen} onOpenChange={setInviteOpen} onSent={loadInvites} />
      <DeclineDialog dealer={declining} onOpenChange={(o) => !o && setDeclining(null)} onDeclined={onChanged} />
    </section>
  );
}
